import * as palette from "@govlaunch/palette";
import React from "react";
import { FormattedMessage } from "react-intl";
import { ICompanyAccountBillingInfoQuery } from "~/lib/queries/__generated__/CompanyAccountBillingInfoQuery.generated";
import mediaQueries from "~/lib/utils/mediaQueries";
import { useSelfie } from "~/components/auth/Auth";
import getIntercomTarget from "~/components/vendor/profile/getIntercomTarget";
import CurrentBill from "~/components/makers/CurrentBill";
import CurrentBillingCurrentCredits from "~/components/makers/CurrentBillingCurrentCredits";
import CurrentBillingNextPaymentDue from "~/components/makers/CurrentBillingNextPaymentDue";
import CurrentBullingOverdueSubscription from "~/components/makers/CurrentBillingOverdueSubscription";
import CurrentBillingPaymentMethod from "~/components/makers/CurrentBillingPaymentMethod";
import CurrentBillingScheduledToCancel from "~/components/makers/CurrentBillingScheduledToCancel";
import CurrentBillingTrialPeriod from "~/components/makers/CurrentBillingTrialPeriod";

interface ICurrentBillingProps {
  company: NonNullable<ICompanyAccountBillingInfoQuery["company"]>;
}

export default function CurrentBilling({ company }: ICurrentBillingProps) {
  const selfie = useSelfie();

  if (!selfie) {
    return null;
  }

  if (!company.plan.limits.inviteGovernments) {
    return null;
  }

  if (
    company.subscription &&
    company.stripePlan &&
    company.subscription.__typename === "ScheduledToCancelSubscription"
  ) {
    return (
      <CurrentBillingScheduledToCancel
        planName={company.stripePlan.displayName}
        cancellationDate={company.subscription.willCancelAt}
      />
    );
  }

  return (
    <div
      data-intercom-target={getIntercomTarget(selfie, {
        asMember: "Settings Billing Box",
        asOwner: "Settings Billing Box",
        asDisruptor: "Settings Billing Box for Disruptors",
      })}
      css={{
        padding: 24,
        backgroundColor: palette.white,
        border: "solid 0.5px #e6e8ea",
        borderRadius: 4,
      }}
    >
      <h1
        css={{
          margin: 0,
          color: "#212121",
          fontWeight: 600,
          fontSize: 20,
        }}
      >
        <FormattedMessage defaultMessage="Billing" id="Tbo377" />
      </h1>

      <hr
        css={{
          width: "100%",
          marginTop: 20,
          border: "none",
          borderBottom: `solid 1px #e6e8ea`,
        }}
      />

      <div
        css={{
          paddingTop: 20,
        }}
      >
        <div
          css={mediaQueries({
            display: "grid",
            gridTemplateColumns: ["1fr", "1fr", "1fr", "max-content 1fr"],
            gridColumnGap: 40,
            gridRowGap: 30,
          })}
        >
          <CurrentBill
            stripePlan={company.stripePlan}
            freeSubscription={
              company.subscription &&
              company.subscription.__typename === "ActiveSubscription" &&
              company.subscription.freeSubscription
            }
          />

          {company.subscription && company.subscription.__typename === "ActiveSubscription" && (
            <CurrentBillingNextPaymentDue subscription={company.subscription} />
          )}

          {company.subscription && company.subscription.__typename === "OverdueSubscription" && (
            <CurrentBullingOverdueSubscription subscription={company.subscription} />
          )}

          {company.subscription &&
            company.subscription.__typename === "ActiveSubscription" &&
            company.subscription.trialPeriod !== null &&
            company.subscription.trialPeriod > 0 && (
              <CurrentBillingTrialPeriod
                trialPeriod={company.subscription!.trialPeriod}
                subscribedAt={company.subscription!.subscribedAt}
              />
            )}

          <CurrentBillingPaymentMethod billing={company.billing} />

          <CurrentBillingCurrentCredits
            balanceTransactions={company.balanceTransactions}
            canEarnCreditsUntil={company.canEarnCreditsUntil}
          />
        </div>
      </div>
    </div>
  );
}
